import { addMinutes } from 'date-fns'
import React from 'react'

import {
  ProcessedEvent,
  getColorByColorName,
  getColorById,
  renderMissionsWithNames,
  renderScenario,
} from 'pages/TaskScheduler/TaskSchedulerUtils'
import { VENDOR } from 'utils/vendorDisplayName'

import type { FilterableRecord } from './filterableRecord'

/**
 * Read-only calendar events from normalized statistics rows.
 */
export function filterableRecordToProcessedEvent(
  r: FilterableRecord,
  robotColorBySn?: Record<string, string | null | undefined>
): ProcessedEvent {
  const end = r.endAt ?? addMinutes(r.startAt, 30)
  let fallbackKey = 0
  for (let i = 0; i < r.robotSn.length; i++) fallbackKey += r.robotSn.charCodeAt(i)
  const colorKey = r.robotId && r.robotId > 0 ? r.robotId : Math.abs(fallbackKey % 1000)
  const robotColorName = robotColorBySn ? robotColorBySn[r.robotSn] : undefined
  const eventColor = robotColorName ? getColorByColorName(robotColorName) : getColorById(colorKey)
  const statusPrefix =
    r.vendor === VENDOR.GAUSIUM
      ? r.endAt
        ? '✔ '
        : '⏳ '
      : r.executionStatus === 4
      ? '✔ '
      : r.executionStatus === 255
      ? '？ '
      : r.executionStatus === 7
      ? '⛔ '
      : r.executionStatus === 8
      ? '⚠️ '
      : r.executionStatus === 99
      ? ''
      : '⏳ '
  const rawTaskIdPart = r.taskId != null ? String(r.taskId) : r.sourceEventId
  const taskIdPart =
    r.vendor === VENDOR.GAUSIUM && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(rawTaskIdPart)
      ? rawTaskIdPart.split('-')[0]
      : rawTaskIdPart
  const useUnifiedTitle = r.vendor === VENDOR.AUTONOMICS || r.vendor === VENDOR.GAUSIUM
  const title =
    useUnifiedTitle
      ? `${statusPrefix}Robot ${r.robotSn} | Task ${taskIdPart} | ${r.displayName}`
      : r.displayName
  const description = React.createElement(
    React.Fragment,
    {},
    r.scenarioId ? renderScenario(r.scenarioId) : null,
    r.vendor === VENDOR.GAUSIUM && r.mapId
      ? React.createElement(
          'div',
          { style: { marginBottom: '2px' } },
          React.createElement('span', { style: { fontWeight: 'bold' } }, 'Map: '),
          String(r.mapId)
        )
      : null,
    r.missionsInfo?.length && r.missionsInfo.length <= 1
      ? renderMissionsWithNames(r.missionsInfo as Array<{ id: number; name: string }>)
      : null
  )
  const ev: ProcessedEvent = {
    event_id: `norm:${r.sourceEventId}`,
    title,
    start: r.startAt,
    end,
    color: eventColor,
    description,
  }
  ev.extendedProps = {
    vendor: r.vendor,
    robot_sn: r.robotSn,
    robot_id: r.robotId,
    facility_ids: r.facilityIds,
    mission_id: r.missionId,
    missions:
      Array.isArray((r.overallProgress as any)?.missions) && (r.overallProgress as any).missions.length > 0
        ? (r.overallProgress as any).missions
        : r.missionsInfoIds,
    missions_info: r.missionsInfo,
    map_id: r.mapId,
    scenario_id: r.scenarioId,
    normalized: true,
    task_id: r.taskId,
    task_type: r.vendorOriginRecordType === 'odyssey_event_statistics' ? 'Odyssey' : 'Task',
    mission_name: r.displayName,
    status: 'executed',
    mission_status: r.executionStatus,
    overall_progress: r.overallProgress || undefined,
    missions_progress: Array.isArray(r.missionsProgress) ? r.missionsProgress : [],
  }
  return ev
}
